import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { useLazyQuery, useMutation } from "@apollo/client";

import { Box, IconButton, Typography } from "@material-ui/core";
import { ExpandLess, ExpandMore } from "@material-ui/icons";

import { ADD_OR_UPDATE_NARRATIVE_SEGMENT } from "../graphql/AddOrUpdateNarrativeSegment";
import { GET_NARRATIVE_SEGMENTS } from "../graphql/GetNarrativeSegments";
import { REMOVE_NARRATIVE_SEGMENT } from "../graphql/RemoveNarrativeSegment";
import { SET_NARRATIVE_SEGMENT_LOCK_STATUS } from "../graphql/SetNarrativeSegmentLockStatus";

import NarrativeSegment from "./NarrativeSegment";
import NarrativeSegmentButtons from "./NarrativeSegmentButtons";
import NarrativeSegmentInput from "./NarrativeSegmentInput";
import NarrativeSegmentEditModal from "./NarrativeSegmentEditModal";
import { INCIDENT_ACTIONS } from "../../../Actions/IncidentActions";
import { handleUuid, nowServerFormat } from "../../../utils/utils";

const NarrativeSegmentsContainer = ({
  blocked,
  roles,
  isOnIncident,
  user,
  disabled,
  createdIncident,
  incidentId = null,
  handleNarrativeSegmentSubscription,
  setPopper,
  online = true,
  nightMode = false,
  incidentDispatch,
  narrativeSegments = [],
}) => {
  const {
    SET_DISPATCH_NARRATIVES,
    ADD_OR_UPDATE_DISPATCH_NARRATIVE,
    REMOVE_DISPATCH_NARRATIVE,
  } = INCIDENT_ACTIONS;

  // State variables
  const [dispatchNarrativeHeight, setDispatchNarrativeHeight] =
    useState("30vh");
  const [newNarrative, setNewNarrative] = useState("");
  const [segmentToEdit, setSegmentToEdit] = useState(null);

  // Apollo Client queries and mutations
  const [getNarrativeSegments] = useLazyQuery(GET_NARRATIVE_SEGMENTS, {
    context: { serializationKey: "incidentQueue" },
    variables: { incidentId, originatorId: user?.id },
    fetchPolicy: "network-only",
    onCompleted: data => {
      const arr = data?.initialCallInfo?.narrativeSegments || [];
      const reversedSegments = arr.slice().reverse();
      incidentDispatch({
        type: SET_DISPATCH_NARRATIVES,
        payload: {
          incidentId: incidentId,
          segments: reversedSegments,
        },
      });
    },
    onError: error => {
      console.error("Error fetching narrative segments:", error);
    },
  });

  const [addOrUpdateNarrativeSegment] = useMutation(
    ADD_OR_UPDATE_NARRATIVE_SEGMENT,
    {
      context: { serializationKey: "incidentQueue" },
      onCompleted: data => {
        if (data?.addOrUpdateNarrativeSegment?.narrativeSegment) {
          let updatedSegment = {
            ...data.addOrUpdateNarrativeSegment.narrativeSegment,
          };
          updatedSegment.uuid =
            data?.addOrUpdateNarrativeSegment?.narrativeSegmentUuid;
          incidentDispatch({
            type: ADD_OR_UPDATE_DISPATCH_NARRATIVE,
            payload: {
              incidentId: incidentId,
              segment: updatedSegment,
            },
          });
        }
      },
    }
  );

  const [removeNarrativeSegment] = useMutation(REMOVE_NARRATIVE_SEGMENT, {
    context: { serializationKey: "incidentQueue" },
    onCompleted: data => {
      if (data.removeNarrativeSegment?.errors?.length > 0) {
        //join errors with comma and pass to setPopper
        const errorMessage = data.removeNarrativeSegment.errors.join(", ");
        setPopper({
          open: true,
          content: errorMessage,
        });
        console.error(
          "Error removing narrative segment:",
          data.removeNarrativeSegment.errors
        );
        return;
      }
      if (data.removeNarrativeSegment?.narrativeSegmentUuid) {
        incidentDispatch({
          type: REMOVE_DISPATCH_NARRATIVE,
          payload: {
            incidentId: incidentId,
            segmentId: data.removeNarrativeSegment.narrativeSegmentUuid,
          },
        });
      }
    },
    onError: error => {
      console.error("Error removing narrative segment:", error);
    },
  });

  const [setNarrativeSegmentLockStatus] = useMutation(
    SET_NARRATIVE_SEGMENT_LOCK_STATUS,
    {
      context: { serializationKey: "incidentQueue" },
      onCompleted: data => {
        if (data.setNarrativeSegmentLockStatus?.errors?.length > 0) {
          data.setNarrativeSegmentLockStatus.errors.forEach(err => {
            if (err.includes("This narrative segment is already locked")) {
              setPopper({
                open: true,
                content:
                  err +
                  "\nYou'll be notified when it is available for editing.",
              });
            }
          });
          console.error(
            "onCompleted: error setting narrative segment lock status:",
            data.setNarrativeSegmentLockStatus.errors
          );
          return;
        }
        const foundSegment = narrativeSegments.find(
          segment =>
            segment.id ===
            data.setNarrativeSegmentLockStatus.narrativeSegmentUuid
        );
        if (foundSegment) {
          let updatedSegment = { ...foundSegment };
          updatedSegment.locked = data.setNarrativeSegmentLockStatus.locked;
          incidentDispatch({
            type: ADD_OR_UPDATE_DISPATCH_NARRATIVE,
            payload: {
              incidentId: incidentId,
              segment: updatedSegment,
            },
          });
        }
      },
      onError: error => {
        console.error(
          "onError: error setting narrative segment lock status:",
          error
        );
      },
    }
  );

  // Handlers for narrative segment operations
  const handleUpdateNewNarrative = e => {
    setNewNarrative(e.target.value);
  };

  const handleCreateNarrative = () => {
    if (newNarrative.trim() === "") {
      alert("Narrative content cannot be empty.");
      return;
    }
    const segmentUuid = handleUuid();
    if (!online) {
      const now = nowServerFormat();
      incidentDispatch({
        type: ADD_OR_UPDATE_DISPATCH_NARRATIVE,
        payload: {
          incidentId,
          segment: {
            id: segmentUuid,
            content: newNarrative.trim(),
            createdAt: now,
            lastUpdatedAt: now,
            officer: user?.officer,
            lastUpdatedBy: user?.officer,
            locked: false,
            incident: {
              id: incidentId,
            },
          },
        },
      });
    }
    setNewNarrative("");
    addOrUpdateNarrativeSegment({
      variables: {
        input: {
          incidentId,
          content: newNarrative.trim(),
          originatorId: user?.id,
          narrativeSegmentId: segmentUuid,
        },
      },
    });
  };

  const handleRequestEdit = segment => {
    handleNarrativeSegmentSubscription(
      segment.id,
      "subscribe",
      getNarrativeSegments
    );
    setNarrativeSegmentLockStatus({
      variables: {
        input: {
          narrativeSegmentId: segment.id,
          originatorId: user?.id,
          locked: true,
        },
      },
    }).then(res => {
      if (
        res.data.setNarrativeSegmentLockStatus?.errors?.length > 0 ||
        res.data.setNarrativeSegmentLockStatus?.locked === false
      ) {
        console.error(
          "Error locking narrative segment for editing:",
          res.data.setNarrativeSegmentLockStatus.errors
        );
        return;
      }

      let localSegment = { ...segment };
      localSegment.locked = true;
      setSegmentToEdit(localSegment);
    });
  };

  const handleUpdateNarrative = updatedContent => {
    if (segmentToEdit && updatedContent.trim() !== "") {
      if (!online) {
        incidentDispatch({
          type: ADD_OR_UPDATE_DISPATCH_NARRATIVE,
          payload: {
            incidentId,
            segment: {
              ...segmentToEdit,
              content: updatedContent.trim(),
              lastUpdatedAt: nowServerFormat(),
              lastUpdatedBy: user?.officer,
            },
          },
        });
      }
      addOrUpdateNarrativeSegment({
        variables: {
          input: {
            incidentId,
            content: updatedContent.trim(),
            originatorId: user?.id,
            narrativeSegmentId: segmentToEdit.id,
          },
        },
      }).then(() => {
        setNarrativeSegmentLockStatus({
          variables: {
            input: {
              narrativeSegmentId: segmentToEdit.id,
              originatorId: user?.id,
              locked: false,
            },
          },
        });

        handleNarrativeSegmentSubscription(segmentToEdit.id, "unsubscribe");
      });
      setSegmentToEdit(null);
    } else {
      alert("Narrative content cannot be empty.");
      return;
    }
  };

  const handleAbortEditing = () => {
    if (segmentToEdit?.id) {
      setNarrativeSegmentLockStatus({
        variables: {
          input: {
            narrativeSegmentId: segmentToEdit.id,
            originatorId: user?.id,
            locked: false,
          },
        },
      });

      handleNarrativeSegmentSubscription(segmentToEdit.id, "unsubscribe");
      setSegmentToEdit(null);
    }
  };

  const handleRemoveNarrativeSegment = segmentId => {
    if (
      segmentId &&
      window.confirm("Are you sure you want to remove this narrative segment?")
    ) {
      if (!online) {
        incidentDispatch({
          type: REMOVE_DISPATCH_NARRATIVE,
          payload: {
            incidentId,
            segmentId,
          },
        });
        return;
      }

      removeNarrativeSegment({
        variables: {
          input: {
            originatorId: user?.id,
            narrativeSegmentId: segmentId,
          },
        },
      });
    } else {
      alert("Segment ID is required to remove a narrative segment.");
    }
  };

  // Effect to fetch narrative segments when component mounts or incidentId/user?.id changes
  useEffect(() => {
    if (incidentId && user?.id) {
      getNarrativeSegments();
    }
  }, [getNarrativeSegments, incidentId, user?.id]);

  return (
    <>
      <NarrativeSegmentInput
        blocked={blocked}
        roles={roles}
        isOnIncident={isOnIncident}
        userId={user?.id}
        onChange={handleUpdateNewNarrative}
        value={newNarrative}
        disabled={disabled}
        onSave={handleCreateNarrative}
        onCancel={() => setNewNarrative("")}
        createdIncident={createdIncident}
      />
      <Box display="flex" flexDirection="row" alignItems="center">
        <Typography
          style={{
            lineHeight: "1.0",
            color: nightMode ? "#ffffffb3" : "rgba(0, 0, 0, 0.54)",
          }}
        >
          {"Dispatch\xa0Narratives"}{" "}
        </Typography>
        <IconButton
          edge="start"
          onClick={() =>
            dispatchNarrativeHeight === "30vh"
              ? setDispatchNarrativeHeight("fit-content")
              : setDispatchNarrativeHeight("30vh")
          }
          style={{ margin: 0 }}
        >
          {dispatchNarrativeHeight === "30vh" ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <div
        style={{
          maxHeight: dispatchNarrativeHeight,
          overflow: "auto",
          border: "1px solid gray",
          borderRadius: "5px",
        }}
        id="narratives-container-id"
        data-testid="narratives-container-id"
      >
        {narrativeSegments?.length > 0 ? (
          narrativeSegments.map((segment, index) => {
            const isLast = index === narrativeSegments.length - 1;
            return (
              <NarrativeSegment
                key={segment.id}
                segment={segment}
                userId={user?.id}
                isLast={isLast}
                nightMode={nightMode}
              >
                <NarrativeSegmentButtons
                  userId={user?.id}
                  createdById={segment?.officer?.id}
                  segment={segment}
                  onEdit={handleRequestEdit}
                  onDelete={handleRemoveNarrativeSegment}
                  isOffline={!online}
                />
              </NarrativeSegment>
            );
          })
        ) : (
          <div />
        )}
      </div>
      <NarrativeSegmentEditModal
        open={!!segmentToEdit}
        onChange={e => {
          setSegmentToEdit(prev => ({
            ...prev,
            content: e.target.value,
          }));
        }}
        segment={segmentToEdit}
        value={segmentToEdit?.content || ""}
        userId={user?.id}
        onSave={handleUpdateNarrative}
        onClose={handleAbortEditing}
      />
    </>
  );
};

NarrativeSegmentsContainer.propTypes = {
  blocked: PropTypes.bool,
  roles: PropTypes.arrayOf(PropTypes.string),
  isOnIncident: PropTypes.bool,
  user: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  createdIncident: PropTypes.bool,
  incidentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleNarrativeSegmentSubscription: PropTypes.func,
  setPopper: PropTypes.func,
  online: PropTypes.bool,
  subscriptionServer: PropTypes.object,
  nightMode: PropTypes.bool,
  incidentDispatch: PropTypes.func.isRequired,
};
export default NarrativeSegmentsContainer;
